import type { ThemePreference } from "../progress/schema";

export const COSMETIC_VISUAL_IDS = [
  "gloam",
  "lime",
  "coral",
  "indigo-tide",
  "jade-circuit",
  "constellation",
] as const;

export type CosmeticVisualId = (typeof COSMETIC_VISUAL_IDS)[number];

export type CosmeticPattern =
  "signal" | "ember" | "tide" | "circuit" | "constellation" | "plain";

export interface CosmeticRenderStyle {
  readonly body: string;
  readonly shade: string;
  readonly plate: string;
  readonly edge: string;
  readonly accent: string;
  readonly motifOutline: string;
  readonly node: string;
  readonly facet: string;
  readonly dash: readonly number[];
  readonly pattern: CosmeticPattern;
  readonly glowBlur: number;
}

export interface DomThemeTokens {
  readonly background: string;
  readonly surface: string;
  readonly raisedSurface: string;
  readonly text: string;
  readonly mutedText: string;
  readonly accent: string;
  readonly accentText: string;
  readonly accentContrast: string;
  readonly danger: string;
  readonly dangerText: string;
  readonly dangerContrast: string;
  readonly structure: string;
  readonly focus: string;
}

export interface DomPresentationTokens {
  readonly subtleBorder: string;
  readonly mintFill: string;
  readonly mintText: string;
  readonly mintContrast: string;
  readonly control: string;
  readonly panelGlass: string;
  readonly hudGlass: string;
  readonly hudBorder: string;
  readonly chartSurface: string;
  readonly gridLine: string;
  readonly chartDash: string;
  readonly glowAccent: string;
  readonly glowDanger: string;
  readonly scrim: string;
  readonly debug: string;
  readonly debugBorder: string;
  readonly backgroundRadialMint: string;
  readonly backgroundRadialDanger: string;
  readonly coilBase: string;
  readonly coilSignal: string;
  readonly coilEye: string;
}

export interface CanvasPalette {
  readonly background: string;
  readonly grid: string;
  readonly gridMarker: string;
  readonly ambient: string;
  readonly arena: string;
  readonly arenaGlow: string;
  readonly tide: string;
  readonly tideGlow: string;
  readonly food: {
    readonly natural: string;
    readonly core: string;
    readonly boost: string;
    readonly elimination: string;
    readonly resonant: string;
  };
  readonly bots: readonly string[];
  readonly eye: string;
  readonly protection: string;
  readonly debug: string;
}

export interface ThemeContract {
  readonly dom: DomThemeTokens;
  readonly presentation: DomPresentationTokens;
  readonly canvas: CanvasPalette;
  readonly cosmetics: Readonly<Record<CosmeticVisualId, CosmeticRenderStyle>>;
}

export const THEME_CONTRACT: Readonly<Record<ThemePreference, ThemeContract>> =
  {
    dark: {
      dom: {
        background: "#08130F",
        surface: "#0D1B16",
        raisedSurface: "#12231D",
        text: "#E7E4D7",
        mutedText: "#A9B5AD",
        accent: "#B7D867",
        accentText: "#B7D867",
        accentContrast: "#162014",
        danger: "#D97464",
        dangerText: "#D97464",
        dangerContrast: "#211412",
        structure: "#71877D",
        focus: "#B7D867",
      },
      presentation: {
        subtleBorder: "color-mix(in srgb, #71877D 30%, transparent)",
        mintFill: "#78C9AF",
        mintText: "#78C9AF",
        mintContrast: "#14251F",
        control: "rgb(17 26 23 / 42%)",
        panelGlass: "rgb(25 35 31 / 88%)",
        hudGlass: "rgb(17 26 23 / 78%)",
        hudBorder: "rgb(228 224 212 / 36%)",
        chartSurface:
          "linear-gradient(145deg, rgb(25 35 31 / 94%), rgb(17 26 23 / 68%))",
        gridLine: "rgb(120 201 175 / 8%)",
        chartDash: "rgb(120 201 175 / 36%)",
        glowAccent: "rgb(190 214 110 / 18%)",
        glowDanger: "rgb(228 123 105 / 34%)",
        scrim: "rgb(5 10 8 / 70%)",
        debug: "#FF9AEE",
        debugBorder: "rgb(255 126 219 / 56%)",
        backgroundRadialMint: "rgb(50 108 86 / 20%)",
        backgroundRadialDanger: "rgb(228 123 105 / 8%)",
        coilBase: "#29453B",
        coilSignal: "#BED66E",
        coilEye: "#08130F",
      },
      canvas: {
        background: "#08130F",
        grid: "rgba(113, 135, 125, 0.075)",
        gridMarker: "rgba(113, 135, 125, 0.25)",
        ambient: "#122A20",
        arena: "#D97464",
        arenaGlow: "rgba(217, 116, 100, 0.16)",
        tide: "rgba(183, 216, 103, 0.62)",
        tideGlow: "rgba(183, 216, 103, 0.52)",
        food: {
          natural: "#90B29B",
          core: "#DCE5C5",
          boost: "#D97464",
          elimination: "#E7E4D7",
          resonant: "#B7D867",
        },
        bots: ["#D97464", "#86B8A4", "#CBD0C7", "#8297CF", "#C79D63"],
        eye: "#08130F",
        protection: "#9DCBD0",
        debug: "#FF9AEE",
      },
      cosmetics: {
        gloam: {
          body: "#6EAC88",
          shade: "#244736",
          plate: "#ACCEAD",
          edge: "#5C9474",
          accent: "#E7E4D7",
          motifOutline: "#08130F",
          node: "#E7E4D7",
          facet: "#526D19",
          dash: [],
          pattern: "plain",
          glowBlur: 2,
        },
        lime: {
          body: "#397862",
          shade: "#1A4436",
          plate: "#78B592",
          edge: "#67B49A",
          accent: "#D6EC72",
          motifOutline: "#08130F",
          node: "#E7E4D7",
          facet: "#285B4C",
          dash: [],
          pattern: "signal",
          glowBlur: 2,
        },
        coral: {
          body: "#C25F50",
          shade: "#60302B",
          plate: "#E99B81",
          edge: "#BD7C66",
          accent: "#F1AA79",
          motifOutline: "#261512",
          node: "#F7E0CF",
          facet: "#2A211D",
          dash: [0.16, 1.05],
          pattern: "ember",
          glowBlur: 2,
        },
        "indigo-tide": {
          body: "#4B6097",
          shade: "#263252",
          plate: "#89A0CF",
          edge: "#879BCD",
          accent: "#BDEFF3",
          motifOutline: "#080B14",
          node: "#DCE7DE",
          facet: "#596FAE",
          dash: [],
          pattern: "tide",
          glowBlur: 2,
        },
        "jade-circuit": {
          body: "#39705A",
          shade: "#1D4237",
          plate: "#72A88B",
          edge: "#C9A766",
          accent: "#F4D98B",
          motifOutline: "#071712",
          node: "#E5D5A7",
          facet: "#356F59",
          dash: [1.1, 0.3, 0.18, 0.3],
          pattern: "circuit",
          glowBlur: 2,
        },
        constellation: {
          body: "#D8D4C5",
          shade: "#34313F",
          plate: "#F0E8D2",
          edge: "#8D82B5",
          accent: "#9C8BCB",
          motifOutline: "#3E315F",
          node: "#E7E4D7",
          facet: "#6E70B8",
          dash: [0.16, 1.05],
          pattern: "constellation",
          glowBlur: 2,
        },
      },
    },
    light: {
      dom: {
        background: "#EEEBDD",
        surface: "#F1EDE1",
        raisedSurface: "#F7F2E5",
        text: "#18302B",
        mutedText: "#52645D",
        accent: "#8EAD43",
        accentText: "#526D19",
        accentContrast: "#18302B",
        danger: "#B65A4A",
        dangerText: "#8D4035",
        dangerContrast: "#231815",
        structure: "#71877D",
        focus: "#315F52",
      },
      presentation: {
        subtleBorder: "color-mix(in srgb, #71877D 30%, transparent)",
        mintFill: "#8FCFB9",
        mintText: "#236B58",
        mintContrast: "#153B31",
        control: "rgb(243 240 230 / 76%)",
        panelGlass: "rgb(243 240 230 / 92%)",
        hudGlass: "rgb(250 247 237 / 88%)",
        hudBorder: "rgb(23 33 30 / 34%)",
        chartSurface:
          "linear-gradient(145deg, rgb(243 240 230 / 98%), rgb(221 225 214 / 86%))",
        gridLine: "rgb(48 83 69 / 12%)",
        chartDash: "rgb(35 107 88 / 38%)",
        glowAccent: "rgb(79 104 0 / 12%)",
        glowDanger: "rgb(157 69 56 / 24%)",
        scrim: "rgb(27 35 32 / 54%)",
        debug: "#8A176F",
        debugBorder: "rgb(138 23 111 / 58%)",
        backgroundRadialMint: "rgb(73 123 103 / 14%)",
        backgroundRadialDanger: "rgb(157 69 56 / 8%)",
        coilBase: "#B5C1B7",
        coilSignal: "#4F6800",
        coilEye: "#F7F2E5",
      },
      canvas: {
        background: "#EEEBDD",
        grid: "rgba(113, 135, 125, 0.11)",
        gridMarker: "rgba(82, 100, 93, 0.3)",
        ambient: "#D8DFCB",
        arena: "#B65A4A",
        arenaGlow: "rgba(182, 90, 74, 0.12)",
        tide: "rgba(82, 100, 93, 0.58)",
        tideGlow: "rgba(142, 173, 67, 0.42)",
        food: {
          natural: "#52645D",
          core: "#F7F2E5",
          boost: "#B65A4A",
          elimination: "#30463F",
          resonant: "#526D19",
        },
        bots: ["#9C493C", "#316956", "#43534D", "#465D91", "#8A5A2F"],
        eye: "#F7F2E5",
        protection: "#356B72",
        debug: "#7A235F",
      },
      cosmetics: {
        gloam: {
          body: "#467654",
          shade: "#253C2B",
          plate: "#95BB93",
          edge: "#2F573E",
          accent: "#18302B",
          motifOutline: "#F7F2E5",
          node: "#F7F2E5",
          facet: "#526D19",
          dash: [],
          pattern: "plain",
          glowBlur: 0,
        },
        lime: {
          body: "#39765E",
          shade: "#203F32",
          plate: "#80B38A",
          edge: "#244F43",
          accent: "#C7E85A",
          motifOutline: "#08130F",
          node: "#F7F2E5",
          facet: "#2E6655",
          dash: [],
          pattern: "signal",
          glowBlur: 0,
        },
        coral: {
          body: "#B65A4A",
          shade: "#60302B",
          plate: "#DF957D",
          edge: "#71362E",
          accent: "#F5B08B",
          motifOutline: "#24110E",
          node: "#F7F2E5",
          facet: "#302522",
          dash: [0.16, 1.05],
          pattern: "ember",
          glowBlur: 0,
        },
        "indigo-tide": {
          body: "#4B6097",
          shade: "#263252",
          plate: "#8598C6",
          edge: "#303E66",
          accent: "#BDEFF3",
          motifOutline: "#0B101A",
          node: "#F7F2E5",
          facet: "#516BA7",
          dash: [],
          pattern: "tide",
          glowBlur: 0,
        },
        "jade-circuit": {
          body: "#39705A",
          shade: "#203F35",
          plate: "#76A58A",
          edge: "#23483B",
          accent: "#E5D5A7",
          motifOutline: "#071712",
          node: "#F7F2E5",
          facet: "#3F7963",
          dash: [1.1, 0.3, 0.18, 0.3],
          pattern: "circuit",
          glowBlur: 0,
        },
        constellation: {
          body: "#5D5974",
          shade: "#34313F",
          plate: "#D8CBAA",
          edge: "#34323F",
          accent: "#A996DA",
          motifOutline: "#3B285B",
          node: "#F7F2E5",
          facet: "#596CB5",
          dash: [0.16, 1.05],
          pattern: "constellation",
          glowBlur: 0,
        },
      },
    },
  };

export const THEME_SURFACES: Readonly<Record<ThemePreference, string>> = {
  dark: THEME_CONTRACT.dark.dom.background,
  light: THEME_CONTRACT.light.dom.background,
};

export const CANVAS_PALETTES: Readonly<Record<ThemePreference, CanvasPalette>> =
  {
    dark: THEME_CONTRACT.dark.canvas,
    light: THEME_CONTRACT.light.canvas,
  };

export const getCosmeticStyle = (
  theme: ThemePreference,
  cosmetic: string,
): CosmeticRenderStyle => {
  const resolved = COSMETIC_VISUAL_IDS.includes(cosmetic as CosmeticVisualId)
    ? (cosmetic as CosmeticVisualId)
    : "gloam";
  return THEME_CONTRACT[theme].cosmetics[resolved];
};

const cssVariables = (theme: ThemeContract): Readonly<Record<string, string>> =>
  ({
    "--color-bg": theme.dom.background,
    "--line-soft": theme.presentation.subtleBorder,
    "--color-surface": theme.dom.surface,
    "--color-surface-raised": theme.dom.raisedSurface,
    "--color-text": theme.dom.text,
    "--color-text-muted": theme.dom.mutedText,
    "--color-border": theme.dom.structure,
    "--color-accent-fill": theme.dom.accent,
    "--color-accent-text": theme.dom.accentText,
    "--color-accent-contrast": theme.dom.accentContrast,
    "--color-danger-fill": theme.dom.danger,
    "--color-danger-text": theme.dom.dangerText,
    "--color-danger-contrast": theme.dom.dangerContrast,
    "--color-focus": theme.dom.focus,
    "--color-mint-fill": theme.presentation.mintFill,
    "--color-mint-text": theme.presentation.mintText,
    "--color-mint-contrast": theme.presentation.mintContrast,
    "--color-control": theme.presentation.control,
    "--color-panel-glass": theme.presentation.panelGlass,
    "--color-hud-glass": theme.presentation.hudGlass,
    "--color-hud-border": theme.presentation.hudBorder,
    "--color-chart-surface": theme.presentation.chartSurface,
    "--color-grid-line": theme.presentation.gridLine,
    "--color-chart-dash": theme.presentation.chartDash,
    "--color-glow-accent": theme.presentation.glowAccent,
    "--color-glow-danger": theme.presentation.glowDanger,
    "--color-scrim": theme.presentation.scrim,
    "--color-debug": theme.presentation.debug,
    "--color-debug-border": theme.presentation.debugBorder,
    "--color-bg-radial-mint": theme.presentation.backgroundRadialMint,
    "--color-bg-radial-danger": theme.presentation.backgroundRadialDanger,
    "--color-coil-base": theme.presentation.coilBase,
    "--color-coil-signal": theme.presentation.coilSignal,
    "--color-coil-eye": theme.presentation.coilEye,
    "--cosmetic-gloam": theme.cosmetics.gloam.body,
    "--cosmetic-coral": theme.cosmetics.coral.body,
    "--cosmetic-lime": theme.cosmetics.lime.body,
    "--cosmetic-indigo-tide": theme.cosmetics["indigo-tide"].body,
    "--cosmetic-jade-circuit": theme.cosmetics["jade-circuit"].body,
    "--cosmetic-constellation": theme.cosmetics.constellation.body,
  }) as const;

export const applyTheme = (
  theme: ThemePreference,
  target: Document = document,
): void => {
  const contract = THEME_CONTRACT[theme];
  target.documentElement.dataset.theme = theme;
  target.documentElement.style.colorScheme = theme;
  for (const [name, value] of Object.entries(cssVariables(contract))) {
    target.documentElement.style.setProperty(name, value);
  }
  target
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute("content", contract.dom.background);
};
